import Builder from 'react-native-jala-orm/src/model/Builder';
import _ from 'lodash';
import Expression from 'react-native-jala-orm/src/query/Expression';
import SyncableModel from './SyncableModel';
import Cycle from './Cycle';
import File from './File';

export default class Mortality extends SyncableModel {
    static name = 'Mortality'

    _fillable = [
      'id', 'cycle_id', 'quantity', 'average_weight',
      'recorded_at', 'multiplier', 'remark',
      'created_at', 'updated_at', 'deleted_at', 'synced_at',
    ]

    static _childs = ['files'];

    static _parents = ['cycle'];

    static endpoint = '/mortalities'

    static allowReplaceUpdate = true

    _casts = {
      quantity: 'integer',
      average_weight: 'float',
      age: 'integer',
      total_weight: 'float',
      size: 'float'
    }

    static _boot() {
      super._boot();

      this.addGlobalScope('global', (query: Builder) => {
        if (_.isEmpty(query.getQuery().columns)) {
          query.select([query.qualifyColumn('*')]);
        }

        const cycle = (new Cycle()).setTable(`${query.getModel().getTable()}_cycles`);
        query.leftJoin(
          `${(new Cycle()).getTable()} as ${cycle.getTable()}`,
          cycle.getQualifiedKeyName(), query.qualifyColumn(cycle.getForeignKey())
        );
      });

      this.updating((mortality: Mortality) => {
        if (mortality.isDirty(mortality.getKeyName())) {
          mortality.relationFiles()
            .update({
              fileable_id: mortality.getKey()
            });
        }
      });
    }

    qualifyColumn(column: string): string {
      if (column === '*') {
        return new Expression(
          `${this.getTable()}.*, `
            + `${this.qualifyColumn('total_weight')} as total_weight, `
            + `${this.qualifyColumn('size')} as size, `
            + `${this.qualifyColumn('age')} as age`
        );
      }
      if (column === 'total_weight') {
        return new Expression(
          `(${this.qualifyColumn('quantity')} * ${this.qualifyColumn('average_weight')} / 1000)`
        );
      }
      if (column === 'size') {
        return new Expression(`ROUND(1000 / ${this.qualifyColumn('average_weight')}, 3)`);
      }
      if (column === 'age') {
        const cycle = (new Cycle()).setTable(`${this.getTable()}_cycles`);
        return new Expression(
          '('
            + 'CAST('
              + `(JULIANDAY(${this.qualifyColumn('recorded_at')}) - `
              + `JULIANDAY(${cycle.qualifyColumn('started_at')})) AS INTEGER`
            + `) + COALESCE(${cycle.qualifyColumn('initial_age')}, 0)`
          + ')'
        );
      }
      return super.qualifyColumn(column);
    }

    /**
     * @param value
     * @returns {Mortality}
     */
    setTotalWeightAttribute(value) {
      if ('quantity' in this._attributes && this._attributes.quantity) {
        this._attributes = {
          ...this._attributes,
          average_weight: (value * 1000) / this._attributes.quantity
        };
      } else if ('average_weight' in this._attributes && this._attributes.average_weight) {
        this._attributes = {
          ...this._attributes,
          quantity: Math.round((value * 1000) / this._attributes.average_weight)
        };
      }
      this.offsetUnset('total_weight');
      this.offsetUnset('size');
      return this;
    }

    /**
     * @param value
     * @returns {Mortality}
     */
    setSizeAttribute(value) {
      this._attributes = {
        ...this._attributes,
        average_weight: 1000 / value
      };
      this.offsetUnset('total_weight');
      this.offsetUnset('size');
      return this;
    }

    /**
     * Get total_weight attribute
     * @param value
     * @returns {*}
     */
    getTotalWeightAttribute(value) {
      let val = value;
      if ('total_weight' in this._attributes) val = this._attributes.total_weight;
      if (val) return this._castAttribute('total_weight', val);
      this._attributes = {
        ...this._attributes,
        total_weight: (
          this._attributes.average_weight * this._attributes.quantity
        ) / 1000
      };
      this.syncOriginalAttribute('total_weight');
      return this._attributes.total_weight;
    }

    /**
     * Get size attribute
     * @param value
     * @returns {*}
     */
    getSizeAttribute(value) {
      let val = value;
      if ('size' in this._attributes) val = this._attributes.size;
      if (val) return this._castAttribute('size', val);
      this._attributes = {
        ...this._attributes,
        size: 1000 / (this._attributes.average_weight || 1)
      };
      this.syncOriginalAttribute('size');
      return this._attributes.size;
    }

    relationCycle() {
      return Cycle.query().whereKey(this.cycle_id);
    }

    relationFiles() {
      return File.query()
        .where(new File().qualifyColumn('fileable_id'), this.getOriginal(this.getKeyName()) || this.getKey())
        .where(new File().qualifyColumn('fileable_type'), 'App\\Mortality');
    }
}
